/**
 * Sign in with Apple (web JS SDK) for use with Firebase Auth OAuthProvider('apple.com').
 * Loads https://appleid.cdn-apple.com/.../appleid.auth.js on demand.
 */

const APPLE_SCRIPT_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

declare global {
  interface Window {
    AppleID?: {
      auth?: {
        init: (opts: Record<string, unknown>) => Promise<void>;
        signIn: () => Promise<unknown>;
      };
    };
  }
}

let appleScriptPromise: Promise<void> | null = null;

export function loadAppleAuthJs(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.AppleID?.auth?.init) return Promise.resolve();
  if (appleScriptPromise) return appleScriptPromise;
  appleScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = APPLE_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Apple Sign In script'));
    document.head.appendChild(s);
  });
  return appleScriptPromise;
}

export async function sha256Hex(message: string): Promise<string> {
  const data = new TextEncoder().encode(message);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function randomNonce(byteLength = 32): string {
  const a = new Uint8Array(byteLength);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
}

export type AppleWebTokenResult = { idToken: string; rawNonce: string };

/**
 * Runs Apple JS popup; returns ID token + raw nonce for Firebase `OAuthProvider('apple.com').credential`.
 */
export async function getAppleIdTokenForFirebaseAuth(opts: {
  clientId: string;
  redirectURI: string;
}): Promise<AppleWebTokenResult> {
  await loadAppleAuthJs();
  const AppleIDAuth = window.AppleID?.auth;
  if (!AppleIDAuth?.init || !AppleIDAuth.signIn) {
    throw new Error('Apple Sign In is not available in this browser.');
  }

  const rawNonce = randomNonce();
  const hashedNonce = await sha256Hex(rawNonce);

  await AppleIDAuth.init({
    clientId: opts.clientId,
    scope: 'name email',
    redirectURI: opts.redirectURI,
    usePopup: true,
    nonce: hashedNonce,
  });

  const res = await AppleIDAuth.signIn();
  type AppleAuthPayload = { authorization?: { id_token?: string } };
  const auth =
    res && typeof res === 'object' && 'authorization' in res
      ? (res as AppleAuthPayload).authorization
      : undefined;
  const idToken = auth?.id_token;
  if (!idToken) {
    throw new Error(
      'Apple did not return an ID token. Check Services ID and redirect URI in Apple / Firebase.'
    );
  }

  return { idToken, rawNonce };
}
